/* ============================================================
   Erasia — product.js
   Gallery, lightbox, variant picker, product form (AJAX),
   sticky add-to-cart. Custom elements, no dependencies.
   ============================================================ */

(function () {
  'use strict';

  /* ---------- Gallery ---------- */
  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('.product-gallery__slide'));
      this.thumbs = Array.from(this.querySelectorAll('.product-gallery__thumb'));
      this.lightbox = document.getElementById('ProductLightbox-' + this.dataset.section);
      this.index = 0;

      this.thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => this.show(i)));
      this.querySelectorAll('[data-gallery-prev]').forEach((b) => b.addEventListener('click', (e) => { e.stopPropagation(); this.show(this.index - 1); }));
      this.querySelectorAll('[data-gallery-next]').forEach((b) => b.addEventListener('click', (e) => { e.stopPropagation(); this.show(this.index + 1); }));

      const main = this.querySelector('.product-gallery__main');
      if (main && this.lightbox) {
        main.addEventListener('click', () => this.openLightbox());
        this.lightbox.querySelectorAll('[data-lightbox-prev]').forEach((b) => b.addEventListener('click', () => this.show(this.index - 1)));
        this.lightbox.querySelectorAll('[data-lightbox-next]').forEach((b) => b.addEventListener('click', () => this.show(this.index + 1)));
        this.lightbox.querySelectorAll('[data-lightbox-thumb]').forEach((t, i) => t.addEventListener('click', () => this.show(i)));
        this.lightbox.addEventListener('click', (e) => { if (e.target === this.lightbox) this.lightbox.close(); });
      }

      /* swipe */
      let startX = null;
      if (main) {
        main.addEventListener('touchstart', (e) => { startX = e.touches[0].clientX; }, { passive: true });
        main.addEventListener('touchend', (e) => {
          if (startX == null) return;
          const dx = e.changedTouches[0].clientX - startX;
          if (Math.abs(dx) > 40) this.show(this.index + (dx < 0 ? 1 : -1));
          startX = null;
        }, { passive: true });
      }
    }

    openLightbox() {
      if (this.lightbox && typeof this.lightbox.showModal === 'function') this.lightbox.showModal();
    }

    show(i) {
      const n = this.slides.length;
      if (!n) return;
      this.index = ((i % n) + n) % n;
      this.slides.forEach((s, j) => s.classList.toggle('is-active', j === this.index));
      this.thumbs.forEach((t, j) => {
        t.classList.toggle('is-active', j === this.index);
        t.setAttribute('aria-current', j === this.index ? 'true' : 'false');
      });
      this.querySelectorAll('.product-gallery__dots span').forEach((d, j) => d.classList.toggle('is-active', j === this.index));
      if (this.lightbox) {
        this.lightbox.querySelectorAll('[data-lightbox-slide]').forEach((s, j) => { s.hidden = j !== this.index; });
        this.lightbox.querySelectorAll('[data-lightbox-thumb]').forEach((t, j) => t.classList.toggle('is-active', j === this.index));
      }
    }

    /* jump to the slide showing a given media id (variant image switching) */
    showMedia(mediaId) {
      const i = this.slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
      if (i >= 0) this.show(i);
    }
  }
  if (!customElements.get('product-gallery')) customElements.define('product-gallery', ProductGallery);

  /* ---------- Variant picker ---------- */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.productData = JSON.parse(document.getElementById('ProductJSON-' + this.sectionId).textContent);
      this.addEventListener('change', this.onChange.bind(this));
    }

    get selectedOptions() {
      return Array.from(this.querySelectorAll('fieldset')).map(
        (set) => set.querySelector('input:checked')?.value
      );
    }

    onChange() {
      const options = this.selectedOptions;
      const variant = this.productData.variants.find((v) =>
        v.options.every((opt, i) => opt === options[i])
      );
      this.updateSelectedLabels();
      this.dispatchEvent(new CustomEvent('variant:change', {
        bubbles: true,
        detail: { variant: variant || null, sectionId: this.sectionId }
      }));
      if (variant) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }

    updateSelectedLabels() {
      this.querySelectorAll('fieldset').forEach((set) => {
        const label = set.querySelector('[data-selected-value]');
        const checked = set.querySelector('input:checked');
        if (label && checked) label.textContent = checked.value;
      });
    }
  }
  if (!customElements.get('variant-picker')) customElements.define('variant-picker', VariantPicker);

  /* ---------- Product form ---------- */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.idInput = this.form.querySelector('[name="id"]');
      this.submitButton = this.form.querySelector('[type="submit"]');
      this.buyNowButton = this.querySelector('[data-buy-now]');
      this.form.addEventListener('submit', this.onSubmit.bind(this));
      if (this.buyNowButton) this.buyNowButton.addEventListener('click', this.onBuyNow.bind(this));
      document.addEventListener('variant:change', this.onVariantChange.bind(this));
    }

    onVariantChange(event) {
      if (event.detail.sectionId !== this.dataset.section) return;
      const variant = event.detail.variant;
      const priceEl = document.getElementById('ProductPrice-' + this.dataset.section);
      const skuEl = document.getElementById('ProductSku-' + this.dataset.section);

      /* Buy now mirrors availability (Audit 5) */
      if (this.buyNowButton) this.buyNowButton.disabled = !variant || !variant.available;
      this.updateStickyBar(variant);
      this.updateInventoryNotice(variant);
      this.updateGalleryBadge(variant);

      if (!variant) {
        this.toggleButton(false, window.variantStrings.unavailable);
        return;
      }
      this.idInput.value = variant.id;
      this.toggleButton(variant.available, variant.available ? null : window.variantStrings.soldOut);

      if (priceEl) {
        let html = '<span class="price__current">' + window.eraMoney(variant.price) + '</span>';
        if (variant.compare_at_price > variant.price) {
          const off = Math.round((1 - variant.price / variant.compare_at_price) * 100);
          html += ' <span class="price__compare">' + window.eraMoney(variant.compare_at_price) + '</span>' +
                  ' <span class="price__badge-sale">' + off + '% off</span>';
        }
        priceEl.innerHTML = html;
      }
      if (skuEl) skuEl.textContent = variant.sku || '';

      if (variant.featured_media) {
        const gallery = document.querySelector('product-gallery[data-section="' + this.dataset.section + '"]');
        if (gallery) gallery.showMedia(variant.featured_media.id);
      }
    }

    /* Sticky bar mirrors main button state + price (Audit 6) */
    updateStickyBar(variant) {
      document.querySelectorAll('[data-sticky-atc-price]').forEach((el) => {
        el.textContent = variant ? window.eraMoney(variant.price) : window.variantStrings.unavailable;
      });
      document.querySelectorAll('[data-sticky-submit]').forEach((btn) => {
        if (!variant) {
          btn.disabled = true;
          btn.textContent = window.variantStrings.unavailable;
        } else if (!variant.available) {
          btn.disabled = true;
          btn.textContent = window.variantStrings.soldOut;
        } else {
          btn.disabled = false;
          btn.textContent = window.variantStrings.addToCart;
        }
      });
    }

    /* Low-stock / in-stock notice follows the variant (Audit 7) */
    updateInventoryNotice(variant) {
      const wrap = document.querySelector('[data-inventory-notice]');
      if (!wrap) return;
      let map = {};
      const jsonEl = document.querySelector('[data-inventory-json]');
      try { map = jsonEl ? JSON.parse(jsonEl.textContent) : {}; } catch (e) { map = {}; }
      wrap.textContent = '';
      if (!variant) return;
      const info = map[String(variant.id)];
      const threshold = Number(wrap.dataset.threshold || 0);
      const build = (ok, text) => {
        const box = document.createElement('div');
        box.className = 'inventory-notice' + (ok ? ' inventory-notice--ok' : '');
        const dot = document.createElement('span');
        dot.className = 'inventory-notice__dot';
        dot.setAttribute('aria-hidden', 'true');
        const label = document.createElement('span');
        label.className = 'inventory-notice__text';
        label.textContent = text;
        box.appendChild(dot);
        box.appendChild(label);
        wrap.appendChild(box);
      };
      if (info && info.managed && info.qty > 0 && info.qty <= threshold) {
        let text = (wrap.dataset.onlyLeftTemplate || 'Only [count] left').replace('[count]', info.qty);
        if (wrap.dataset.urgency) text += ' — ' + wrap.dataset.urgency;
        build(false, text);
      } else if (wrap.dataset.showInStock === 'true' && variant.available) {
        build(true, wrap.dataset.inStockText || '');
      }
    }

    /* Gallery sale badge follows the variant (Audit 8) */
    updateGalleryBadge(variant) {
      const badge = document.querySelector('.product-gallery[data-section="' + this.dataset.section + '"] [data-gallery-badge]');
      if (!badge) return;
      if (variant && variant.compare_at_price > variant.price) {
        const off = Math.round((1 - variant.price / variant.compare_at_price) * 100);
        badge.textContent = off + '% off';
        badge.hidden = false;
      } else {
        badge.hidden = true;
      }
    }

    toggleButton(enabled, text) {
      if (!this.submitButton) return;
      this.submitButton.toggleAttribute('disabled', !enabled);
      const label = this.submitButton.querySelector('[data-atc-text]') || this.submitButton;
      label.textContent = text || this.submitButton.dataset.defaultLabel || window.variantStrings.addToCart;
    }

    collectBody() {
      const formData = new FormData(this.form);
      const body = { items: [{ id: Number(formData.get('id')), quantity: Number(formData.get('quantity') || 1) }] };
      /* line item properties */
      const props = {};
      formData.forEach((value, key) => {
        const match = key.match(/^properties\[(.+)\]$/);
        if (match && value) props[match[1]] = value;
      });
      if (Object.keys(props).length) body.items[0].properties = props;
      return body;
    }

    onSubmit(event) {
      event.preventDefault();
      if (this.submitButton.hasAttribute('disabled')) return;
      if (this.submitButton.hasAttribute('aria-busy')) return;
      this.submitButton.classList.add('is-loading');
      this.submitButton.setAttribute('aria-busy', 'true');
      window.eraCart.add(this.collectBody())
        .then(() => {
          window.eraToast(this.dataset.addedMessage || window.variantStrings.addToCart);
          window.eraAfterAdd();
        })
        .catch((err) => window.eraToast(err.message))
        .finally(() => {
          this.submitButton.classList.remove('is-loading');
          this.submitButton.removeAttribute('aria-busy');
        });
    }

    onBuyNow(event) {
      event.preventDefault();
      if (event.currentTarget.disabled) return;
      if (this.submitButton && this.submitButton.hasAttribute('disabled')) return;
      window.eraCart.add(this.collectBody())
        .then(() => { window.location.href = '/checkout'; })
        .catch((err) => window.eraToast(err.message));
    }
  }
  if (!customElements.get('product-form')) customElements.define('product-form', ProductForm);

  /* ---------- Sticky add to cart ---------- */
  const stickyBar = document.querySelector('.sticky-atc');
  const anchor = document.querySelector('[data-sticky-anchor]');
  if (stickyBar && anchor) {
    const mobileMq = window.matchMedia('(max-width: 749px)');
    /* Desktop: appear once the buy box scrolls out of view */
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        if (mobileMq.matches) return;
        entries.forEach((entry) => stickyBar.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0));
      }, { threshold: 0 });
      io.observe(anchor);
    }
    /* Mobile: stays visible throughout the page after a short scroll (Issue 14) */
    const syncMobile = () => {
      if (mobileMq.matches) stickyBar.classList.toggle('is-visible', window.scrollY > 200);
    };
    window.addEventListener('scroll', syncMobile, { passive: true });
    if (mobileMq.addEventListener) mobileMq.addEventListener('change', syncMobile);
    syncMobile();
    stickyBar.querySelector('[data-sticky-submit]')?.addEventListener('click', (e) => {
      if (e.currentTarget.disabled) return;
      /* Click the REAL Add to Cart button instead of calling requestSubmit().
         requestSubmit() fires a submit event with no `submitter` and dispatches no
         click, so third-party scripts that listen for the add-to-cart click (e.g.
         Releasit, which re-injects its COD button into the re-rendered cart drawer)
         never see the add. Clicking the real button makes the sticky bar behave
         identically to every other add-to-cart path. */
      const realAtc = document.querySelector('product-form form [type="submit"]');
      if (realAtc && !realAtc.disabled) realAtc.click();
      else document.querySelector('product-form form')?.requestSubmit();
    });
  }

  /* ---------- Size guide modal ---------- */
  document.addEventListener('click', (e) => {
    const open = e.target.closest('[data-modal-open]');
    if (open) {
      const modal = document.getElementById(open.dataset.modalOpen);
      if (modal && typeof modal.showModal === 'function') modal.showModal();
    }
    const close = e.target.closest('[data-modal-close]');
    if (close) close.closest('dialog')?.close();
    /* click on backdrop */
    if (e.target.matches('dialog.modal')) e.target.close();
  });

  /* ---------- track for recently viewed ---------- */
  const rvMeta = document.querySelector('[data-product-handle]');
  if (rvMeta && window.eraRecentlyViewed) window.eraRecentlyViewed.track(rvMeta.dataset.productHandle);
})();
